import { useReducer, useCallback, createContext, useContext } from 'react';

// Action types
export const OFFER_ACTIONS = {
  // Form data actions
  UPDATE_FIELD: 'UPDATE_FIELD',
  UPDATE_MULTIPLE_FIELDS: 'UPDATE_MULTIPLE_FIELDS',
  SET_FORM_DATA: 'SET_FORM_DATA',
  RESET_FORM: 'RESET_FORM',
  
  // Step management
  SET_STEP: 'SET_STEP',
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  
  // File management
  SET_IMAGES: 'SET_IMAGES',
  SET_PDF_FILES: 'SET_PDF_FILES',
  
  // Modal state
  SET_MODAL_OPEN: 'SET_MODAL_OPEN',
  
  // Loading states
  SET_LOADING: 'SET_LOADING',
  SET_SUBMITTING: 'SET_SUBMITTING',
  
  // Edit mode
  SET_CURRENT_DEAL: 'SET_CURRENT_DEAL',
  LOAD_DEAL_FOR_EDIT: 'LOAD_DEAL_FOR_EDIT',
  
  // Validation
  SET_ERRORS: 'SET_ERRORS',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  
  // Form synchronization
  SYNC_FORM: 'SYNC_FORM',
  
  // Form reset
  RESET_FORM_FIELDS: 'RESET_FORM_FIELDS'
};

// Initial state
const initialState = {
  // Form data
  formData: {
    offer: '',
    localisation: null,
    description: '',
    tags: [],
    tagsDescription: [],
    isPassNeeded: false,
    images: [],
    filePdf: [],
    startdate: '',
    enddate: '',
    starttime: '',
    endtime: '',
    isPerma: false,
    isNonPreciseTimeChecked: false,
    mode: '',
    days: [],
    pages: []
  },
  
  // UI state
  step: 0,
  isModalOpen: false,
  isLoading: false,
  isSubmitting: false,
  
  // Edit mode
  currentDeal: null,
  
  // Validation
  errors: {},
  
  // File states
  imgList: [],
  fileList: []
};

// Reducer function
const offerReducer = (state, action) => {
  switch (action.type) {
    case OFFER_ACTIONS.UPDATE_FIELD:
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.field]: action.payload.value
        }
      };
      
    case OFFER_ACTIONS.UPDATE_MULTIPLE_FIELDS:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload
        }
      };
      
    case OFFER_ACTIONS.SET_FORM_DATA:
      return {
        ...state,
        formData: action.payload
      };
      
    case OFFER_ACTIONS.RESET_FORM:
      return {
        ...state,
        formData: initialState.formData,
        step: 0,
        errors: {},
        imgList: [],
        fileList: []
      };
      
    case OFFER_ACTIONS.SET_STEP:
      return {
        ...state,
        step: action.payload
      };
      
    case OFFER_ACTIONS.NEXT_STEP:
      return {
        ...state,
        step: Math.min(state.step + 1, 2) // Max 3 steps (0, 1, 2)
      };
      
    case OFFER_ACTIONS.PREV_STEP:
      return {
        ...state,
        step: Math.max(state.step - 1, 0)
      };
      
    case OFFER_ACTIONS.SET_IMAGES:
      return {
        ...state,
        formData: {
          ...state.formData,
          images: action.payload
        },
        imgList: action.payload
      };
      
    case OFFER_ACTIONS.SET_PDF_FILES:
      return {
        ...state,
        formData: {
          ...state.formData,
          filePdf: action.payload
        },
        fileList: action.payload
      };
      
    case OFFER_ACTIONS.SET_MODAL_OPEN:
      return {
        ...state,
        isModalOpen: action.payload
      };
      
    case OFFER_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case OFFER_ACTIONS.SET_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.payload
      };
      
    case OFFER_ACTIONS.SET_CURRENT_DEAL:
      return {
        ...state,
        currentDeal: action.payload
      };
      
    case OFFER_ACTIONS.LOAD_DEAL_FOR_EDIT:
      return {
        ...state,
        currentDeal: action.payload,
        formData: {
          ...initialState.formData,
          ...action.payload,
          // Convertir les dates en format dayjs pour Ant Design
          startdate: action.payload.startdate ? action.payload.startdate : '',
          enddate: action.payload.enddate && action.payload.enddate !== '2099-12-31' ? action.payload.enddate : '',
          starttime: action.payload.starttime ? action.payload.starttime : '',
          endtime: action.payload.endtime ? action.payload.endtime : '',
        },
        step: 0
      };
      
    case OFFER_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      };
      
    case OFFER_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        errors: {}
      };
      
    case OFFER_ACTIONS.SYNC_FORM:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload
        }
      };
      
    case OFFER_ACTIONS.RESET_FORM_FIELDS:
      return {
        ...state,
        formData: {
          ...initialState.formData,
          ...action.payload
        }
      };
      
    default:
      return state;
  }
};

// Context
const OfferContext = createContext();

// Provider component
export const OfferProvider = ({ children }) => {
  const [state, dispatch] = useReducer(offerReducer, initialState);
  
  // Action dispatchers
  const updateField = useCallback((field, value) => {
    dispatch({ type: OFFER_ACTIONS.UPDATE_FIELD, payload: { field, value } });
  }, []);
  
  const updateMultipleFields = useCallback((fields) => {
    dispatch({ type: OFFER_ACTIONS.UPDATE_MULTIPLE_FIELDS, payload: fields });
  }, []);
  
  const setFormData = useCallback((formData) => {
    dispatch({ type: OFFER_ACTIONS.SET_FORM_DATA, payload: formData });
  }, []);
  
  const resetForm = useCallback(() => {
    dispatch({ type: OFFER_ACTIONS.RESET_FORM });
  }, []);
  
  const setStep = useCallback((step) => {
    dispatch({ type: OFFER_ACTIONS.SET_STEP, payload: step });
  }, []);
  
  const nextStep = useCallback(() => {
    dispatch({ type: OFFER_ACTIONS.NEXT_STEP });
  }, []);
  
  const prevStep = useCallback(() => {
    dispatch({ type: OFFER_ACTIONS.PREV_STEP });
  }, []);
  
  const setImages = useCallback((images) => {
    dispatch({ type: OFFER_ACTIONS.SET_IMAGES, payload: images });
  }, []);
  
  const setPdfFiles = useCallback((files) => {
    dispatch({ type: OFFER_ACTIONS.SET_PDF_FILES, payload: files });
  }, []);
  
  const setModalOpen = useCallback((isOpen) => {
    dispatch({ type: OFFER_ACTIONS.SET_MODAL_OPEN, payload: isOpen });
  }, []);
  
  const setLoading = useCallback((isLoading) => {
    dispatch({ type: OFFER_ACTIONS.SET_LOADING, payload: isLoading });
  }, []);
  
  const setSubmitting = useCallback((isSubmitting) => {
    dispatch({ type: OFFER_ACTIONS.SET_SUBMITTING, payload: isSubmitting });
  }, []);
  
  const setCurrentDeal = useCallback((deal) => {
    dispatch({ type: OFFER_ACTIONS.SET_CURRENT_DEAL, payload: deal });
  }, []);
  
  const loadDealForEdit = useCallback((deal) => {
    dispatch({ type: OFFER_ACTIONS.LOAD_DEAL_FOR_EDIT, payload: deal });
  }, []);
  
  const setErrors = useCallback((errors) => {
    dispatch({ type: OFFER_ACTIONS.SET_ERRORS, payload: errors });
  }, []);
  
  const clearErrors = useCallback(() => {
    dispatch({ type: OFFER_ACTIONS.CLEAR_ERRORS });
  }, []);
  
  const syncForm = useCallback((formData) => {
    dispatch({ type: OFFER_ACTIONS.SYNC_FORM, payload: formData });
  }, []);
  
  const resetFormFields = useCallback((formData) => {
    dispatch({ type: OFFER_ACTIONS.RESET_FORM_FIELDS, payload: formData });
  }, []);
  
  const value = {
    // State
    ...state,
    
    // Actions
    updateField,
    updateMultipleFields,
    setFormData,
    resetForm,
    setStep,
    nextStep,
    prevStep,
    setImages,
    setPdfFiles,
    setModalOpen,
    setLoading,
    setSubmitting,
    setCurrentDeal,
    loadDealForEdit,
    setErrors,
    clearErrors,
    syncForm,
    resetFormFields
  };
  
  return (
    <OfferContext.Provider value={value}>
      {children}
    </OfferContext.Provider>
  );
};

// Custom hook to use the context
export const useOfferReducer = () => {
  const context = useContext(OfferContext);
  if (!context) {
    throw new Error('useOfferReducer must be used within an OfferProvider');
  }
  return context;
}; 